import { useState, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Spinner,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input
} from '@heroui/react';
import { Group } from '@visx/group';
import { Bar } from '@visx/shape';
import { scaleLinear, scaleBand } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import SendersByCountry from './SendersByCountry';

const AnalyticsModal = ({ user, dismissModal, orgId }) => {
  const [campaignData, setCampaignData] = useState(null);
  const [memberData, setMemberData] = useState(null);
  const [topCampaigns, setTopCampaigns] = useState(null);
  const [topCampaignsScope, setTopCampaignsScope] = useState('all'); // all, year, month, week
  const [showScopeSelector, setShowScopeSelector] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [activeQuickRange, setActiveQuickRange] = useState(7); // Track active quick button
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7); // Default to 7 days ago
    return date.toISOString().slice(0, 16);
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().slice(0, 16);
  });
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = async (customStartDate, customEndDate) => {
    setLoading(true);
    try {
      const startTime = new Date(customStartDate || startDate).toISOString();
      const endTime = new Date(customEndDate || endDate).toISOString();

      // Build scope params
      let scopeParams = `scope=${topCampaignsScope}`;
      if (topCampaignsScope === 'year') {
        scopeParams += `&year=${selectedYear}`;
      } else if (topCampaignsScope === 'month') {
        scopeParams += `&year=${selectedYear}&month=${selectedMonth}`;
      } else if (topCampaignsScope === 'week' && selectedWeek) {
        scopeParams += `&week_start=${selectedWeek}`;
      }

      const [campaignRes, memberRes, topRes] = await Promise.all([
        fetch(`/api/v1/organizations/${orgId}/analytics/campaigns?start_time=${startTime}&end_time=${endTime}`),
        fetch(`/api/v1/organizations/${orgId}/analytics/members?start_time=${startTime}&end_time=${endTime}`),
        fetch(`/api/v1/organizations/${orgId}/analytics/top_campaigns?${scopeParams}`)
      ]);

      const campaignJson = await campaignRes.json();
      const memberJson = await memberRes.json();
      const topJson = await topRes.json();

      setCampaignData(campaignJson);
      setMemberData(memberJson);
      setTopCampaigns(topJson);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAnalytics();
  }, [orgId, topCampaignsScope, selectedYear, selectedMonth, selectedWeek]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 10; i--) {
      years.push(i);
    }
    return years;
  };

  const generateMonthOptions = () => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months.map((name, idx) => ({ name, value: idx }));
  };

  const generateWeekOptions = () => {
    const weeks = [];
    const now = new Date();
    for (let i = 0; i < 52; i++) {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - (i * 7));
      weekStart.setHours(0, 0, 0, 0);
      const weekStartDay = weekStart.getDay();
      const diff = weekStart.getDate() - weekStartDay + (weekStartDay === 0 ? -6 : 1);
      weekStart.setDate(diff);
      weeks.push(weekStart.toISOString().split('T')[0]);
    }
    return weeks;
  };

  const getScopeLabel = () => {
    if (topCampaignsScope === 'all') return 'of All Time';
    if (topCampaignsScope === 'year') return selectedYear;
    if (topCampaignsScope === 'month') {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[selectedMonth]} ${selectedYear}`;
    }
    if (topCampaignsScope === 'week' && selectedWeek) {
      return `Week of ${selectedWeek}`;
    }
    return '';
  };

  const setQuickRange = (days) => {
    const end = new Date();
    const start = new Date();

    if (days === 1) {
      // For 24h, go back exactly 24 hours from now
      start.setHours(start.getHours() - 24);
    } else {
      // For other ranges, go back N days
      start.setDate(start.getDate() - days);
    }

    const startStr = start.toISOString().slice(0, 16);
    const endStr = end.toISOString().slice(0, 16);

    setStartDate(startStr);
    setEndDate(endStr);
    setActiveQuickRange(days);

    // Fetch with the new dates immediately
    fetchAnalytics(startStr, endStr);
  };

  const Chart = ({ data, title, granularity, colors = ['#19b2c3', '#10b981', '#8b5cf6', '#ef4444', '#f59e0b'] }) => {
    const width = 800;
    const height = 200;
    const margin = { top: 20, right: 20, bottom: 40, left: 60 };
    const xMax = width - margin.left - margin.right;
    const yMax = height - margin.top - margin.bottom;

    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center h-48 text-black text-sm">
          No data available for this time period
        </div>
      );
    }

    // Prepare chart data based on granularity
    const chartData = [];
    const isHourly = granularity === 'hourly';

    if (isHourly) {
      // Hourly data
      for (let hour = 0; hour < 24; hour++) {
        const hourData = { label: hour };
        data.forEach((item, index) => {
          const hourCount = item.hourly_data?.find(h => h.hour === hour)?.count || 0;
          hourData[`series${index}`] = hourCount;
        });
        chartData.push(hourData);
      }
    } else {
      // Daily data - use the dates from the first item
      const firstItem = data[0];
      const dataPoints = firstItem.daily_data || [];

      dataPoints.forEach((point, idx) => {
        const dayData = { label: point.date };
        data.forEach((item, index) => {
          const dayCount = item.daily_data?.[idx]?.count || 0;
          dayData[`series${index}`] = dayCount;
        });
        chartData.push(dayData);
      });
    }

    const maxValue = Math.max(...chartData.flatMap(d =>
      Object.keys(d).filter(k => k.startsWith('series')).map(k => d[k])
    ), 1);

    const xScale = scaleBand({
      range: [0, xMax],
      domain: chartData.map(d => d.label),
      padding: 0.1,
    });

    const yScale = scaleLinear({
      range: [yMax, 0],
      domain: [0, maxValue],
    });

    const barWidth = xScale.bandwidth() / data.length;

    return (
      <div>
        <svg width={width} height={height}>
          <Group left={margin.left} top={margin.top}>
            {chartData.map(d => (
              <Group key={d.label}>
                {data.map((item, seriesIndex) => (
                  <Bar
                    key={`${d.label}-${seriesIndex}`}
                    x={(xScale(d.label) || 0) + seriesIndex * barWidth}
                    y={yScale(d[`series${seriesIndex}`] || 0)}
                    width={barWidth}
                    height={yMax - yScale(d[`series${seriesIndex}`] || 0)}
                    fill={colors[seriesIndex % colors.length]}
                  />
                ))}
              </Group>
            ))}
            <AxisBottom
              top={yMax}
              scale={xScale}
              tickFormat={label => isHourly ? `${label}h` : label}
              stroke="#374151"
              tickStroke="#374151"
              tickLabelProps={() => ({
                fill: '#374151',
                fontSize: 10,
                textAnchor: 'middle',
              })}
            />
            <AxisLeft
              scale={yScale}
              stroke="#374151"
              tickStroke="#374151"
              tickLabelProps={() => ({
                fill: '#374151',
                fontSize: 10,
                textAnchor: 'end',
                dx: '-0.25em',
                dy: '0.25em',
              })}
            />
          </Group>
        </svg>

        {/* Legend */}
        <div className="mt-4 space-y-2">
          {data.map((item, index) => (
            <div key={item.id || item.user_id || index} className="flex items-center space-x-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: colors[index % colors.length] }}
              />
              <span className="text-sm text-black">
                {item.subject || item.email}
                {item.total_count !== undefined && (
                  <span className="text-gray-600 text-xs ml-2">({item.total_count} emails)</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const TopCampaignsChart = ({ data }) => {
    const [sortColumn, setSortColumn] = useState('email_count');
    const [sortDirection, setSortDirection] = useState('desc');

    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center h-48 text-black text-sm">
          No campaigns found
        </div>
      );
    }

    const calculateSendRatio = (clicks, emailsSent) => {
      if (!clicks || clicks === 0) return 0;
      return (emailsSent / clicks) * 100;
    };

    const handleSort = (column) => {
      if (sortColumn === column) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortColumn(column);
        setSortDirection('desc');
      }
    };

    const sortedData = [...data].sort((a, b) => {
      let aVal, bVal;

      if (sortColumn === 'send_ratio') {
        aVal = calculateSendRatio(a.click_count, a.email_count);
        bVal = calculateSendRatio(b.click_count, b.email_count);
      } else if (sortColumn === 'subject') {
        aVal = a.subject.toLowerCase();
        bVal = b.subject.toLowerCase();
      } else {
        aVal = a[sortColumn];
        bVal = b[sortColumn];
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    const SortIcon = ({ column }) => {
      if (sortColumn !== column) return <span className="text-gray-400 ml-1">↕</span>;
      return <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
    };

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-gray-300 bg-gray-50">
              <th className="text-left py-2 px-2 text-black font-semibold">#</th>
              <th
                className="text-left py-2 px-2 text-black font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('subject')}
              >
                Campaign <SortIcon column="subject" />
              </th>
              <th
                className="text-right py-2 px-2 text-black font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('email_count')}
              >
                Email Records <SortIcon column="email_count" />
              </th>
              <th
                className="text-right py-2 px-2 text-black font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('click_count')}
              >
                Click Count <SortIcon column="click_count" />
              </th>
              <th
                className="text-right py-2 px-2 text-black font-semibold cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('send_ratio')}
              >
                Send % <SortIcon column="send_ratio" />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((campaign, index) => {
              const sendRatio = calculateSendRatio(campaign.click_count, campaign.email_count);
              return (
                <tr key={campaign.id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="py-2 px-2 text-black">{index + 1}</td>
                  <td className="py-2 px-2 text-black">{campaign.subject}</td>
                  <td className="py-2 px-2 text-right text-black font-medium">{campaign.email_count.toLocaleString()}</td>
                  <td className="py-2 px-2 text-right text-black font-medium">{campaign.click_count.toLocaleString()}</td>
                  <td className="py-2 px-2 text-right text-black font-medium">{sendRatio.toFixed(1)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Modal
      size="5xl"
      isOpen={true}
      isDismissable={true}
      onClose={dismissModal}
      scrollBehavior="inside"
      placement="center"
      className="mx-2"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 pr-12 pb-2">
          <div className="flex items-center justify-between w-full">
            <span className="text-base md:text-lg text-black font-semibold">
              Analytics - {user?.organizations?.find(o => o._id === orgId)?.name}
            </span>
          </div>
        </ModalHeader>
        <ModalBody className="overflow-y-auto pt-4" style={{ maxHeight: 'calc(90vh - 100px)' }}>
          {loading ? (
            <div className="flex justify-center p-8">
              <Spinner />
            </div>
          ) : (
            <div className="space-y-6">
              <Card className="bg-gray-50">
                <CardBody>
                  <div className="flex flex-col space-y-2">
                    <div className="flex items-center space-x-2 flex-wrap gap-1">
                      <Input
                        type="datetime-local"
                        label="Start"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          setActiveQuickRange(null);
                        }}
                        size="sm"
                        className="w-44"
                        classNames={{ label: "text-[10px]" }}
                      />
                      <Input
                        type="datetime-local"
                        label="End"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          setActiveQuickRange(null);
                        }}
                        size="sm"
                        className="w-44"
                        classNames={{ label: "text-[10px]" }}
                      />
                      <Button size="sm" color="primary" onPress={fetchAnalytics}>
                        Update
                      </Button>
                    </div>
                    <div className="flex items-center space-x-1 flex-wrap gap-1">
                      <span className="text-xs text-gray-600">Quick:</span>
                      <Button
                        size="sm"
                        variant={activeQuickRange === 1 ? "solid" : "flat"}
                        color={activeQuickRange === 1 ? "primary" : "default"}
                        onPress={() => setQuickRange(1)}
                        className="min-w-0 px-2"
                      >
                        24h
                      </Button>
                      <Button
                        size="sm"
                        variant={activeQuickRange === 7 ? "solid" : "flat"}
                        color={activeQuickRange === 7 ? "primary" : "default"}
                        onPress={() => setQuickRange(7)}
                        className="min-w-0 px-2"
                      >
                        7d
                      </Button>
                      <Button
                        size="sm"
                        variant={activeQuickRange === 30 ? "solid" : "flat"}
                        color={activeQuickRange === 30 ? "primary" : "default"}
                        onPress={() => setQuickRange(30)}
                        className="min-w-0 px-2"
                      >
                        30d
                      </Button>
                      <Button
                        size="sm"
                        variant={activeQuickRange === 90 ? "solid" : "flat"}
                        color={activeQuickRange === 90 ? "primary" : "default"}
                        onPress={() => setQuickRange(90)}
                        className="min-w-0 px-2"
                      >
                        90d
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <h3 className="text-lg md:text-xl font-bold text-black">
                    Last 5 Campaigns - Email Records {campaignData?.granularity === 'hourly' ? 'per Hour' : 'per Day'}
                  </h3>
                </CardHeader>
                <CardBody>
                  <Chart data={campaignData?.campaigns} granularity={campaignData?.granularity} title="Campaigns" />
                </CardBody>
              </Card>

              <div style={{height: '10px'}} />

              <Card>
                <CardHeader>
                  <h3 className="text-lg md:text-xl font-bold text-black">
                    Top 5 Members - Email Records {memberData?.granularity === 'hourly' ? 'per Hour' : 'per Day'}
                  </h3>
                </CardHeader>
                <CardBody>
                  <Chart
                    data={memberData?.members}
                    granularity={memberData?.granularity}
                    title="Members"
                    colors={['#f59e0b', '#ef4444', '#8b5cf6', '#10b981', '#19b2c3']}
                  />
                </CardBody>
              </Card>

              <div style={{height: '10px'}} />

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between w-full">
                    <div>
                      <h3 className="text-lg md:text-xl font-bold text-black">
                        Top 10 Campaigns {getScopeLabel()}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Ranked by email records sent
                      </p>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant={topCampaignsScope === 'all' ? "solid" : "flat"}
                          color={topCampaignsScope === 'all' ? "primary" : "default"}
                          onPress={() => {
                            setTopCampaignsScope('all');
                            setShowScopeSelector(false);
                          }}
                          className="min-w-0 px-2 h-6"
                          style={{ fontSize: '10px' }}
                        >
                          All
                        </Button>
                        <Button
                          size="sm"
                          variant={topCampaignsScope === 'year' ? "solid" : "flat"}
                          color={topCampaignsScope === 'year' ? "primary" : "default"}
                          onPress={() => {
                            setTopCampaignsScope('year');
                            setShowScopeSelector(true);
                          }}
                          className="min-w-0 px-2 h-6"
                          style={{ fontSize: '10px' }}
                        >
                          Year
                        </Button>
                        <Button
                          size="sm"
                          variant={topCampaignsScope === 'month' ? "solid" : "flat"}
                          color={topCampaignsScope === 'month' ? "primary" : "default"}
                          onPress={() => {
                            setTopCampaignsScope('month');
                            setShowScopeSelector(true);
                          }}
                          className="min-w-0 px-2 h-6"
                          style={{ fontSize: '10px' }}
                        >
                          Month
                        </Button>
                        <Button
                          size="sm"
                          variant={topCampaignsScope === 'week' ? "solid" : "flat"}
                          color={topCampaignsScope === 'week' ? "primary" : "default"}
                          onPress={() => {
                            setTopCampaignsScope('week');
                            setShowScopeSelector(true);
                            if (!selectedWeek) {
                              setSelectedWeek(generateWeekOptions()[0]);
                            }
                          }}
                          className="min-w-0 px-2 h-6"
                          style={{ fontSize: '10px' }}
                        >
                          Week
                        </Button>
                      </div>

                      {showScopeSelector && topCampaignsScope === 'year' && (
                        <select
                          value={selectedYear}
                          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                          className="border rounded px-2 py-1 text-black"
                          style={{ fontSize: '11px' }}
                        >
                          {generateYearOptions().map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      )}

                      {showScopeSelector && topCampaignsScope === 'month' && (
                        <div className="flex space-x-1">
                          <select
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                            className="border rounded px-2 py-1 text-black"
                            style={{ fontSize: '11px' }}
                          >
                            {generateMonthOptions().map(month => (
                              <option key={month.value} value={month.value}>{month.name}</option>
                            ))}
                          </select>
                          <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                            className="border rounded px-2 py-1 text-black"
                            style={{ fontSize: '11px' }}
                          >
                            {generateYearOptions().map(year => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {showScopeSelector && topCampaignsScope === 'week' && (
                        <select
                          value={selectedWeek || ''}
                          onChange={(e) => setSelectedWeek(e.target.value)}
                          className="border rounded px-2 py-1 max-h-32 overflow-y-auto text-black"
                          style={{ fontSize: '11px' }}
                        >
                          {generateWeekOptions().map(week => (
                            <option key={week} value={week}>Week of {week}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <TopCampaignsChart data={topCampaigns?.campaigns} />
                </CardBody>
              </Card>

              <div style={{height: '10px'}} />

              <Card>
                <CardBody>
                  <SendersByCountry organizationId={orgId} />
                </CardBody>
              </Card>

              <div style={{height: '10px'}} />

            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AnalyticsModal;
